/** Local, explicit KnowFit simulation data. Never calls Shopify or the cart. */
(function initializeKnowFitTestCatalog(root, factory) {
  const provider = root?.KnowFitTestCatalog || factory(root);
  if (typeof module === 'object' && module.exports) module.exports = provider;
  if (root) root.KnowFitTestCatalog = provider;
})(typeof window !== 'undefined' ? window : globalThis, function createKnowFitTestCatalog(root) {
  'use strict';

  const STORAGE_KEY = 'knoweats:knowfit-test-catalog:v1';
  const MAX_FILE_BYTES = 5 * 1024 * 1024;
  const CHANGE_EVENT = 'knowfit:test-catalog-change';
  const MEAL_TYPES = ['breakfast', 'main', 'snack', 'dinner'];
  const NUTRIENTS = {
    kcal: 'kcal_100g', protein_g: 'proteina_100g', fat_g: 'grasas_100g',
    saturated_fat_g: 'grasas_saturadas_100g', carbs_g: 'carbohidratos_100g',
    sugar_g: 'azucares_100g', fiber_g: 'fibra_100g', salt_g: 'sal_100g',
  };
  const has = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);
  const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

  function fail(message) { throw new Error(message); }

  function engine() {
    const value = root?.KnowFitEngine || (typeof require === 'function' ? require('./knowfit-engine.js') : null);
    if (!value?.normalizeCatalog || !value?.parsePortionWeight || !value?.resolveCatalogSources) {
      fail('No está disponible el motor KnowFit necesario para validar el archivo de prueba.');
    }
    return value;
  }

  function storage() {
    try {
      if (!root?.localStorage) throw new Error('missing storage');
      return root.localStorage;
    } catch {
      fail('No se puede acceder al almacenamiento local de la simulación. El modo de prueba permanece bloqueado.');
    }
  }

  function readStored() {
    try { return storage().getItem(STORAGE_KEY); }
    catch { fail('No se puede leer el catálogo de prueba guardado. Revisa el almacenamiento local antes de continuar.'); }
  }

  // A corrupt or inaccessible record must never fall through to the live cart.
  function isActive() {
    try { return readStored() !== null; }
    catch { return true; }
  }

  function canonical(value) {
    if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
    if (isObject(value)) return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
    return JSON.stringify(value);
  }

  function hash(value, prefix) {
    const text = canonical(value);
    let first = 2166136261;
    let second = 5381;
    for (let index = 0; index < text.length; index += 1) {
      first = Math.imul(first ^ text.charCodeAt(index), 16777619);
      second = Math.imul(second, 33) ^ text.charCodeAt(index);
    }
    return `${prefix}-${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`;
  }

  function fingerprint(catalog, summary) { return hash({ catalog, summary }, 'test-v1'); }

  function decodeRecord(serialized) {
    if (serialized === null) return null;
    let record;
    try { record = JSON.parse(serialized); }
    catch { fail('El catálogo de prueba guardado está dañado. Elimina la simulación o importa de nuevo el JSON.'); }
    if (!isObject(record) || record.format !== 'knowfit.test-catalog' || ![1, 2].includes(record.version)
      || typeof record.file_name !== 'string' || !isObject(record.catalog)
      || !Array.isArray(record.catalog.products) || !isObject(record.summary)) {
      fail('El catálogo de prueba guardado es inválido. Elimina la simulación o importa de nuevo el JSON.');
    }
    if (record.version === 1) {
      if (record.id !== fingerprint(record.catalog, record.summary)
        || engine().normalizeCatalog(record.catalog).products.length === 0) {
        fail('El catálogo de prueba guardado es inválido. Elimina la simulación o importa de nuevo el JSON.');
      }
      const assignments = emptyAssignments();
      const meals = record.summary.meal_types;
      if (!Array.isArray(meals) || meals.length === 0 || meals.some((meal) => !MEAL_TYPES.includes(meal))) {
        fail('Las categorías del catálogo anterior son inválidas. Elimina la simulación e importa de nuevo el JSON.');
      }
      meals.forEach((meal) => {
        const catalog = isolateCatalog(record.catalog, meal);
        assignments[meal] = {
          file_name: record.file_name, imported_at: String(record.imported_at || ''), catalog,
          summary: summarizeCatalog(catalog, [meal], record.summary.shop, record.summary.collection,
            catalog.sources[meal].product_count),
        };
      });
      return buildRecord(assignments, String(record.imported_at || ''));
    }
    try {
      const rebuilt = buildRecord(record.assignments, record.imported_at);
      if (record.id !== rebuilt.id || canonical(record.catalog) !== canonical(rebuilt.catalog)
        || canonical(record.summary) !== canonical(rebuilt.summary) || record.file_name !== rebuilt.file_name) throw new Error('mismatch');
      return rebuilt;
    } catch {
      fail('El catálogo de prueba guardado es inválido. Elimina la simulación y vuelve a importar los archivos.');
    }
  }

  function getRecord() { return decodeRecord(readStored()); }

  function getCatalog() { return getRecord()?.catalog ?? null; }

  function emitChange(record) {
    if (typeof root?.dispatchEvent === 'function' && typeof root?.CustomEvent === 'function') {
      root.dispatchEvent(new root.CustomEvent(CHANGE_EVENT, { detail: { active: record !== null, id: record?.id ?? null } }));
    }
  }

  function beforeChange() {
    if (typeof root?.dispatchEvent === 'function' && typeof root?.CustomEvent === 'function'
      && root.dispatchEvent(new root.CustomEvent('knowfit:before-test-catalog-change', { cancelable: true })) === false) {
      fail('Espera a que termine la operación de KnowFit antes de cambiar el catálogo.');
    }
  }

  function clear() {
    beforeChange();
    try { storage().removeItem(STORAGE_KEY); }
    catch { fail('No se pudo eliminar el catálogo de prueba. La simulación sigue activa.'); }
    emitChange(null);
  }

  function numeric(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || !/^\d+(?:\.\d+)?$/.test(value.trim())) return null;
    return Number.isFinite(Number(value)) ? Number(value) : null;
  }

  function cents(value) {
    const amount = numeric(value);
    const result = amount === null ? null : Math.round(amount * 100);
    return Number.isSafeInteger(result) && result >= 0 ? result : null;
  }

  function adminId(object, kind) {
    const gid = String(object?.id ?? '').match(new RegExp(`^gid://shopify/${kind}/([1-9]\\d*)$`));
    const legacy = object?.legacyResourceId;
    if (typeof legacy === 'number' && !Number.isSafeInteger(legacy)) fail(`El identificador de ${kind} ha perdido precisión.`);
    const id = legacy === null || legacy === undefined ? gid?.[1] : String(legacy);
    if (!id || !/^[1-9]\d*$/.test(id) || (gid && gid[1] !== id)) fail(`Identificador de ${kind} inválido o contradictorio.`);
    return id;
  }

  function nodes(connection, label) {
    if (Array.isArray(connection)) return connection;
    if (isObject(connection) && Array.isArray(connection.nodes)) return connection.nodes;
    fail(`Falta la lista completa de ${label}.`);
  }

  function checkPagination(value) {
    if (!value || typeof value !== 'object') return;
    if (isObject(value.pageInfo) && value.pageInfo.hasNextPage !== false) {
      fail('El JSON contiene una paginación incompleta. Exporta todas las páginas antes de importarlo.');
    }
    Object.values(value).forEach(checkPagination);
  }

  function metafields(connection, label) {
    const map = new Map();
    nodes(connection, label).forEach((field) => {
      if (!isObject(field) || typeof field.namespace !== 'string' || typeof field.key !== 'string') fail(`Metafield inválido en ${label}.`);
      const key = `${field.namespace}.${field.key}`;
      if (map.has(key)) fail(`Metafield duplicado: ${key}.`);
      map.set(key, field);
    });
    return map;
  }

  function fieldValue(field) {
    if (!field) return null;
    return has(field, 'jsonValue') ? field.jsonValue : field.value ?? null;
  }

  function moneyField(field) {
    if (!field) return null;
    let value = fieldValue(field);
    if (field.type === 'money') {
      if (typeof value === 'string') {
        try { value = JSON.parse(value); } catch { return null; }
      }
      if (!isObject(value) || (value.currency_code ?? value.currencyCode) !== 'EUR') return null;
      return cents(value.amount);
    }
    // Numeric one_time_price values use the same cents unit as the Liquid feed.
    const result = numeric(value);
    return Number.isSafeInteger(result) && result >= 0 ? result : null;
  }

  function weightForVariant(variant, fields) {
    if (has(variant, 'selectedOptions')) {
      if (!Array.isArray(variant.selectedOptions)) fail('selectedOptions debe ser una lista.');
      const option = variant.selectedOptions.find((entry) => ['gramaje', 'peso', 'racion'].includes(
        String(entry?.name ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase()
      ));
      if (option) return { value: option.value ?? null, source: 'selected_options' };
    } else {
      const title = String(variant.title ?? '').trim();
      if (/g$/i.test(title) && engine().parsePortionWeight(title) !== null) return { value: title, source: 'variant_title' };
      // An old export with distinct, non-default titles may hide weight options.
      // Never silently assign one product size to these unknown variant sizes.
      if (title && !/^default title$/i.test(title)) return { value: null, source: 'ambiguous_variant_title' };
    }
    const numericWeight = fieldValue(fields.get('custom.gramaje_numero'));
    return {
      value: numericWeight === null || numericWeight === '' ? fieldValue(fields.get('custom.gramaje')) : numericWeight,
      source: numericWeight === null || numericWeight === '' ? 'product_gramaje' : 'product_gramaje_numero',
    };
  }

  function safeImage(value) {
    if (typeof value !== 'string') return '';
    try { const url = new URL(value); return url.protocol === 'https:' ? url.href : ''; } catch { return ''; }
  }

  function fishInfo(product, fields) {
    const indicatorField = product.indicators ?? fields.get('custom.indicaciones');
    if (!indicatorField) return { unknown: true, contains: true, images: [] };
    let ids = fieldValue(indicatorField);
    if (typeof ids === 'string') { try { ids = JSON.parse(ids); } catch { ids = null; } }
    const references = indicatorField.references?.nodes;
    if (Array.isArray(ids) && ids.length === 0 && (!references || references.length === 0)) {
      return { unknown: false, contains: false, images: [] };
    }
    if (!Array.isArray(references) || !Array.isArray(ids) || references.length !== ids.length
      || new Set(ids).size !== ids.length || new Set(references.map((reference) => reference?.id)).size !== ids.length
      || references.some((reference) => !ids.includes(reference?.id))) {
      return { unknown: true, contains: true, images: [] };
    }
    const images = references.map((reference) => {
      const url = safeImage(reference?.image?.url ?? reference?.preview?.image?.url ?? reference?.url);
      let filename = '';
      try { filename = decodeURIComponent(new URL(url).pathname.split('/').pop()).toLowerCase(); } catch { /* Unknown reference. */ }
      return { url, label: filename.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '), filename };
    });
    const unknown = images.some((image) => !image.url || !image.filename);
    return { unknown, contains: unknown || images.some((image) => image.filename === 'pescado.svg'), images: images.filter((image) => image.url).map(({ url, label }) => ({ url, label })) };
  }

  function convertExport(data, selectedMealTypes, allowedTypes = MEAL_TYPES) {
    if (!isObject(data) || data.format !== 'knowfit.admin-collection-export' || data.format_version !== 1) {
      fail('El archivo debe ser un export knowfit.admin-collection-export, versión 1.');
    }
    if (!isObject(data.shop) || data.shop.currencyCode !== 'EUR') fail('La simulación solo admite un export con moneda EUR.');
    if (typeof data.shop.myshopifyDomain !== 'string'
      || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(data.shop.myshopifyDomain)) {
      fail('El export debe identificar la tienda mediante myshopifyDomain.');
    }
    if (!Array.isArray(selectedMealTypes) || selectedMealTypes.length === 0
      || selectedMealTypes.some((value) => !allowedTypes.includes(value))) fail('Selecciona al menos una categoría válida para la colección.');
    const mealTypes = [...new Set(selectedMealTypes)].sort();
    const products = nodes(data.products, 'productos');
    const complete = data.completeness;
    const flags = ['products_pagination_complete', 'variants_pagination_complete', 'custom_product_metafields_pagination_complete', 'variant_metafields_pagination_complete', 'indicator_references_pagination_complete'];
    if (!isObject(complete) || flags.some((key) => complete[key] !== true)) fail('La exportación está incompleta o no acredita todas sus páginas.');
    if (['expected_product_count', 'exported_product_count', 'unique_product_count'].some((key) => complete[key] !== products.length)) fail('Los recuentos de productos no coinciden con el contenido del archivo.');
    checkPagination(data);
    const collectionId = adminId(data.collection, 'Collection');
    const productIds = new Set();
    const variantIds = new Set();
    const rawProducts = [];
    products.forEach((product) => {
      const productId = adminId(product, 'Product');
      if (productIds.has(productId)) fail(`Producto duplicado: ${productId}.`);
      productIds.add(productId);
      const fields = metafields(product.metafields, `producto ${productId}`);
      const variants = nodes(product.variants, `variantes de ${productId}`);
      const fish = fishInfo(product, fields);
      const media = Array.isArray(product.media?.nodes) ? product.media.nodes : [];
      const image = media.map((item) => item?.image ?? item?.preview?.image).find((item) => safeImage(item?.url)) || {};
      const surcharge = moneyField(fields.get('custom.surcharges')) ?? 0;
      variants.forEach((variant) => {
        const variantId = adminId(variant, 'ProductVariant');
        if (variantIds.has(variantId)) fail(`Variante duplicada: ${variantId}.`);
        variantIds.add(variantId);
        const variantFields = metafields(variant.metafields, `variante ${variantId}`);
        const specialPrice = variantFields.get('one_time_price.price');
        const specialPriceValue = fieldValue(specialPrice);
        const price = specialPrice && specialPriceValue !== null && specialPriceValue !== ''
          ? moneyField(specialPrice) : cents(variant.price);
        const weight = weightForVariant(variant, fields);
        rawProducts.push({
          product_id: productId, variant_id: variantId, collection_ids: [collectionId],
          title: String(product.title ?? ''), product_title: String(product.title ?? ''),
          variant_title: String(variant.title ?? ''), handle: String(product.handle ?? ''), url: '',
          image: safeImage(image.url), image_alt: String(image.altText ?? product.title ?? ''),
          image_width: image.width ?? null, image_height: image.height ?? null,
          price_cents: price === null ? 'invalid' : price, compare_at_price_cents: cents(variant.compareAtPrice) ?? 0,
          available: true, published: true, source_status: String(product.status ?? 'UNKNOWN'),
          source_available: typeof variant.availableForSale === 'boolean' ? variant.availableForSale : null,
          source_published_at: product.publishedAt ?? null, source_fish_unknown: fish.unknown,
          meal_type: mealTypes[0], meal_types: mealTypes, contains_fish: fish.contains,
          food_indicators: fish.images, is_premium: surcharge > 0, surcharge_cents: surcharge,
          weight_g: weight.value, variant_weight_source: weight.source,
          nutrition_per_100g: Object.fromEntries(Object.entries(NUTRIENTS).map(([name, key]) => [name, fieldValue(fields.get(`custom.${key}`))])),
        });
      });
    });
    if (complete.exported_variant_count !== rawProducts.length) fail('El recuento de variantes no coincide con el contenido del archivo.');
    rawProducts.sort((first, second) => first.variant_id.localeCompare(second.variant_id));
    const collection = { id: collectionId, title: String(data.collection.title ?? ''), handle: String(data.collection.handle ?? '') };
    const sources = Object.fromEntries(allowedTypes.map((mealType) => [mealType, {
      configured: mealTypes.includes(mealType), assigned: mealTypes.includes(mealType), fallback_from: null,
      id: mealTypes.includes(mealType) ? collectionId : null,
      title: mealTypes.includes(mealType) ? collection.title : '', handle: mealTypes.includes(mealType) ? collection.handle : '',
      product_count: mealTypes.includes(mealType) ? products.length : 0,
    }]));
    const catalog = { sources, products: rawProducts };
    const summary = summarizeCatalog(catalog, mealTypes,
      { name: String(data.shop.name ?? ''), domain: data.shop.myshopifyDomain.toLowerCase(), currency: 'EUR' },
      collection, products.length);
    if (products.length > 0 && summary.valid_variant_count === 0) {
      fail(`Ninguna de las ${rawProducts.length} variantes tiene peso, nutrición y precio válidos. ${summary.rejected[0]?.message ?? ''}`);
    }
    return { catalog, summary };
  }

  function summarizeCatalog(catalog, mealTypes, shop, collection, productCount) {
    const rawProducts = catalog.products;
    const normalized = engine().normalizeCatalog(catalog);
    const rejected = normalized.rejected.map((entry) => ({
      variant_id: rawProducts[entry.index]?.variant_id ?? '', product_id: rawProducts[entry.index]?.product_id ?? '',
      title: rawProducts[entry.index]?.product_title ?? '', code: entry.code, message: entry.message,
    }));
    const fishUnknownCount = rawProducts.filter((product) => product.source_fish_unknown).length;
    const warnings = [];
    if (rejected.length) warnings.push(`${rejected.length} variantes rechazadas por datos incompletos o inválidos; no participarán en la selección.`);
    if (fishUnknownCount) warnings.push(`${fishUnknownCount} variantes sin indicadores de pescado verificables. Se excluyen de los perfiles que no comen pescado; no se ha inferido esta información del nombre.`);
    warnings.push('Simulación local: se permite probar productos DRAFT o no disponibles. Su estado original se conserva; no se modifican Shopify ni el carrito.');
    return {
      product_count: productCount ?? new Set(rawProducts.map((product) => product.product_id)).size,
      variant_count: rawProducts.length, valid_variant_count: normalized.products.length,
      rejected_variant_count: rejected.length, rejected, fish_unknown_count: fishUnknownCount, warnings, meal_types: mealTypes,
      shop, collection,
    };
  }

  function emptyAssignments() { return Object.fromEntries(MEAL_TYPES.map((meal) => [meal, null])); }

  function emptySource() {
    return { configured: false, assigned: false, fallback_from: null, id: null, title: '', handle: '', product_count: 0 };
  }

  function isolateCatalog(catalog, meal) {
    const source = catalog.sources?.[meal] || emptySource();
    return {
      sources: Object.fromEntries(MEAL_TYPES.map((type) => [type, type === meal
        ? { ...source, configured: true, assigned: true, fallback_from: null } : emptySource()])),
      products: catalog.products.filter((product) => (product.meal_types || [product.meal_type]).includes(meal)).map((product) => {
        const { assigned_meal_types, ...data } = product;
        return { ...data, meal_type: meal, meal_types: [meal] };
      }),
    };
  }

  function validateEntry(entry, meal) {
    if (!isObject(entry) || typeof entry.file_name !== 'string' || typeof entry.imported_at !== 'string'
      || !isObject(entry.catalog) || !Array.isArray(entry.catalog.products) || !isObject(entry.catalog.sources)
      || !isObject(entry.summary) || !isObject(entry.summary.shop) || entry.summary.shop.currency !== 'EUR'
      || typeof entry.summary.shop.domain !== 'string'
      || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(entry.summary.shop.domain)
      || !isObject(entry.summary.collection)) {
      fail('Una de las asignaciones del catálogo de prueba es inválida.');
    }
    if (MEAL_TYPES.some((type) => entry.catalog.sources[type]?.assigned !== (type === meal)
      || entry.catalog.sources[type]?.configured !== (type === meal)
      || entry.catalog.sources[type]?.fallback_from !== null)
      || entry.catalog.products.some((product) => !isObject(product) || product.meal_type !== meal
        || canonical(product.meal_types) !== canonical([meal]) || has(product, 'assigned_meal_types'))) {
      fail('Una asignación contiene categorías que no corresponden a su archivo.');
    }
    const productCount = entry.catalog.sources[meal].product_count;
    if (!Number.isSafeInteger(productCount) || productCount < 0) fail('Recuento de productos inválido en una asignación.');
    const expected = summarizeCatalog(entry.catalog, [meal], entry.summary.shop, entry.summary.collection, productCount);
    if (canonical(entry.summary) !== canonical(expected)
      || (productCount > 0 && expected.valid_variant_count === 0)) fail('El resumen de una asignación es inválido.');
  }

  function buildRecord(assignments, importedAt = new Date().toISOString()) {
    if (!isObject(assignments) || Object.keys(assignments).length !== MEAL_TYPES.length
      || MEAL_TYPES.some((meal) => !has(assignments, meal))) fail('Faltan las cuatro asignaciones de la simulación.');
    const meals = MEAL_TYPES.filter((meal) => assignments[meal] !== null);
    if (meals.length === 0) fail('La simulación necesita al menos un archivo asignado.');
    meals.forEach((meal) => validateEntry(assignments[meal], meal));
    const entries = meals.map((meal) => assignments[meal]);
    const shops = new Set(entries.map((entry) => entry.summary.shop.domain.toLowerCase()));
    if (shops.size !== 1) fail('Todos los archivos deben pertenecer a la misma tienda Shopify.');
    const sources = Object.fromEntries(MEAL_TYPES.map((meal) => [meal,
      assignments[meal] ? { ...assignments[meal].catalog.sources[meal] } : emptySource()]));
    const variants = new Map();
    meals.forEach((meal) => assignments[meal].catalog.products.forEach((product) => {
      const { meal_type, meal_types, collection_ids, ...data } = product;
      const previous = variants.get(product.variant_id);
      if (previous && canonical(previous.data) !== canonical(data)) {
        fail(`La variante ${product.variant_id} tiene datos distintos entre archivos. Reexporta las colecciones con los mismos precios, tamaños y datos nutricionales antes de combinarlas.`);
      }
      const merged = previous || { data, meals: new Set(), collections: new Set() };
      merged.meals.add(meal);
      (collection_ids || []).forEach((id) => merged.collections.add(id));
      variants.set(product.variant_id, merged);
    }));
    const products = [...variants.values()].map(({ data, meals: productMeals, collections }) => ({
      ...data, meal_type: [...productMeals].sort()[0], meal_types: [...productMeals].sort(),
      collection_ids: [...collections].sort(),
    })).sort((first, second) => first.variant_id.localeCompare(second.variant_id));
    const catalog = engine().resolveCatalogSources({ sources, products });
    const collections = [...new Map(entries.map((entry) => [entry.summary.collection.id, entry.summary.collection])).values()];
    const collection = collections.length === 1 ? collections[0]
      : { id: '', title: `${collections.length} colecciones de prueba`, handle: '' };
    const summary = summarizeCatalog(catalog,
      MEAL_TYPES.filter((meal) => catalog.sources[meal].configured).sort(), entries[0].summary.shop, collection);
    summary.assigned_meal_types = [...meals].sort();
    summary.collections = collections;
    const semantics = Object.fromEntries(MEAL_TYPES.map((meal) => [meal, assignments[meal]
      ? { catalog: assignments[meal].catalog, summary: assignments[meal].summary } : null]));
    return {
      format: 'knowfit.test-catalog', version: 2, id: hash({ catalog, assignments: semantics }, 'test-v2'),
      file_name: [...new Set(entries.map((entry) => entry.file_name))].join(' · '),
      imported_at: String(importedAt), assignments, catalog, summary,
    };
  }

  function utf8Size(text) {
    let bytes = 0;
    for (const character of text) {
      const value = character.codePointAt(0);
      bytes += value < 128 ? 1 : value < 2048 ? 2 : value < 65536 ? 3 : 4;
      if (bytes > MAX_FILE_BYTES) break;
    }
    return bytes;
  }

  async function readFile(file) {
    if (!file || typeof file.text !== 'function') fail('Selecciona un archivo JSON para la simulación.');
    if (Number(file.size) > MAX_FILE_BYTES) fail('El archivo supera el límite de 5 MiB.');
    let text;
    try { text = await file.text(); } catch { fail('No se pudo leer el archivo seleccionado.'); }
    if (typeof text !== 'string' || utf8Size(text) > MAX_FILE_BYTES) fail('El archivo supera el límite de 5 MiB.');
    let data;
    try { data = JSON.parse(text.replace(/^\uFEFF/, '')); } catch { fail('El archivo no contiene JSON válido.'); }
    return { data, file_name: String(file.name ?? 'catalogo.json') };
  }

  function assertUnchanged(expected) {
    if (readStored() !== expected) {
      fail('El catálogo de prueba ha cambiado en otra pestaña o durante la carga. Vuelve a seleccionar los archivos; se conserva el cambio más reciente.');
    }
  }

  function persist(record, expected) {
    // setItem is atomic: all validation precedes it, so quota/parse failures keep
    // the previously active catalog intact and do not emit an activation event.
    assertUnchanged(expected);
    beforeChange();
    assertUnchanged(expected);
    try {
      if (record) storage().setItem(STORAGE_KEY, JSON.stringify(record));
      else storage().removeItem(STORAGE_KEY);
    }
    catch { fail('No se pudo guardar el catálogo de prueba (espacio o permisos de almacenamiento). El catálogo anterior se conserva.'); }
    emitChange(record);
    return record;
  }

  async function importFiles(filesByMeal) {
    if (!isObject(filesByMeal) || Object.keys(filesByMeal).length === 0
      || Object.keys(filesByMeal).some((meal) => !MEAL_TYPES.includes(meal))) {
      fail('Asigna al menos un archivo a una categoría válida.');
    }
    const previous = readStored();
    const current = decodeRecord(previous);
    const assignments = { ...(current?.assignments || emptyAssignments()) };
    const loaded = await Promise.all(Object.entries(filesByMeal).map(async ([meal, file]) => ({ meal, ...await readFile(file) })));
    const importedAt = new Date().toISOString();
    loaded.forEach(({ meal, data, file_name }) => {
      assignments[meal] = { file_name, imported_at: importedAt, ...convertExport(data, [meal]) };
    });
    return persist(buildRecord(assignments, importedAt), previous);
  }

  // Compatibility API: the previous single-file operation intentionally
  // replaces every assignment instead of retaining unrelated files.
  async function importFile(file, mealTypes = ['main', 'dinner']) {
    if (!Array.isArray(mealTypes) || mealTypes.length === 0 || mealTypes.some((meal) => !MEAL_TYPES.includes(meal))) {
      fail('Selecciona al menos una categoría válida para la colección.');
    }
    const previous = readStored();
    const loaded = await readFile(file);
    const importedAt = new Date().toISOString();
    const assignments = emptyAssignments();
    [...new Set(mealTypes)].forEach((meal) => {
      assignments[meal] = { file_name: loaded.file_name, imported_at: importedAt, ...convertExport(loaded.data, [meal]) };
    });
    return persist(buildRecord(assignments, importedAt), previous);
  }

  function removeFile(mealType) {
    if (!MEAL_TYPES.includes(mealType)) fail('Selecciona una categoría válida para quitar su archivo.');
    const previous = readStored();
    const current = decodeRecord(previous);
    if (!current || current.assignments[mealType] === null) return current;
    const assignments = { ...current.assignments, [mealType]: null };
    return persist(MEAL_TYPES.some((meal) => assignments[meal] !== null) ? buildRecord(assignments) : null, previous);
  }

  // Reuse the importer's strict nutrition/portion conversion without persisting the private catalog.
  function convertAdminExports(exports) {
    const types = [...MEAL_TYPES, 'extra'];
    if (!isObject(exports) || Object.keys(exports).some(type => !types.includes(type))) fail('Catálogo privado inválido.');
    const entries = Object.entries(exports).filter(([, data]) => data !== null)
      .map(([type, data]) => convertExport(data, [type], types));
    if (!entries.length || new Set(entries.map(entry => entry.summary.shop.domain)).size !== 1) fail('El catálogo debe pertenecer a una sola tienda.');
    const catalog = engine().mergeCatalogSnapshots(entries.map(entry => entry.catalog));
    catalog.products = catalog.products.filter(product => ['DRAFT', 'ACTIVE', 'UNLISTED'].includes(product.source_status));
    const normalized = engine().normalizeCatalog(catalog);
    return { id: hash(catalog, 'customer-draft'), file_name: 'Shopify · Draft', catalog,
      summary: { product_count: new Set(catalog.products.map(product => product.product_id)).size,
        variant_count: new Set(catalog.products.map(product => product.variant_id)).size,
        valid_variant_count: normalized.products.length, rejected_variant_count: normalized.rejected.length,
        warnings: [...new Set(entries.flatMap(entry => entry.summary.warnings))] } };
  }

  return Object.freeze({ STORAGE_KEY, MAX_FILE_BYTES, CHANGE_EVENT, isActive, getRecord, getCatalog,
    importFile, importFiles, removeFile, clear, convertAdminExports });
});
